import { useState } from 'react';
import { ChevronDown, Building2, Loader2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/AuthProvider';

/**
 * Tenant switcher dropdown for users with access to multiple tenants.
 * Only renders if the user has 2+ available tenants.
 * Super-admins see all tenants; normal users see only their memberships.
 */
export function TenantSwitcher() {
  const { user, switchTenant } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  // Only show if user has multiple tenants available
  if (!user?.available_tenants || user.available_tenants.length <= 1) {
    return null;
  }

  const currentTenant = user.available_tenants.find(
    t => t.tenant_id === user.tenant_id
  );

  const handleSwitch = async (tenantId: string) => {
    if (tenantId === user.tenant_id) return; // Already on this tenant

    setIsLoading(true);
    try {
      await switchTenant(tenantId);
      // Page will reload after successful switch
    } catch (error) {
      console.error('Failed to switch tenant:', error);
      setIsLoading(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="flex items-center gap-2 h-9 px-3"
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Building2 className="h-4 w-4" />
          )}
          <span className="max-w-[150px] truncate text-sm">
            {currentTenant?.name || user.tenant_id.replace(/_/g, ' ')}
          </span>
          <ChevronDown className="h-3 w-3 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {user.available_tenants.map(tenant => (
          <DropdownMenuItem
            key={tenant.tenant_id}
            onClick={() => handleSwitch(tenant.tenant_id)}
            className={tenant.tenant_id === user.tenant_id ? 'bg-accent' : ''}
            disabled={isLoading}
          >
            <span className="flex-1">{tenant.name}</span>
            {tenant.tenant_id === user.tenant_id && (
              <span className="ml-2 text-xs text-muted-foreground">current</span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
